import React, { useState } from "react";
import PropTypes from "prop-types";

import {
  Button,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import MenuIcon from "@material-ui/icons/Menu";
import HomeIcon from "@material-ui/icons/Home";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import ChromeReaderModeOutlinedIcon from "@material-ui/icons/ChromeReaderModeOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  Redirect,
  useLocation,
} from "react-router-dom";

import ProfileImage from "../assets/images/MT.jpg";

import HomeScreen from "./homeScreen";
import AboutScreen from "./aboutScreen";
import ResumeScreen from "./resumeScreen";
import PortfolioScreen from "./portfolioScreen";
import BlogScreen from "./blogScreen";
import ContactScreen from "./contactScreen";

import ScrollToTop from "../components/ScrollToTop";
import ThemeToggle from "../components/themeToggle";

import { getTranslate, changeLanguage, lang } from "../localization";

import "./index.css";

const drawerWidth = 286;

const useStyles = makeStyles((theme) => ({
  "@keyframes sidebarAura": {
    "0%, 100%": { transform: "translate3d(0,0,0) scale(1)", opacity: .32 },
    "50%": { transform: "translate3d(0,-22px,0) scale(1.08)", opacity: .58 },
  },
  "@keyframes sidebarScan": {
    "0%": { transform: "translateY(-120%)", opacity: 0 },
    "18%": { opacity: .72 },
    "82%": { opacity: .42 },
    "100%": { transform: "translateY(920%)", opacity: 0 },
  },
  "@keyframes profileRingSpin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  "@keyframes profileGlow": {
    "0%, 100%": { opacity: .35, transform: "scale(.94)" },
    "50%": { opacity: .72, transform: "scale(1.05)" },
  },
  "@keyframes navEnter": {
    from: { opacity: 0, transform: theme.direction === "rtl" ? "translate3d(14px,0,0)" : "translate3d(-14px,0,0)" },
    to: { opacity: 1, transform: "translate3d(0,0,0)" },
  },
  "@keyframes activeSweep": {
    "0%": { transform: "translateX(-145%)", opacity: 0 },
    "20%": { opacity: .28 },
    "55%": { opacity: .16 },
    "100%": { transform: "translateX(170%)", opacity: 0 },
  },
  "@keyframes activeIconPulse": {
    "0%, 100%": { filter: "drop-shadow(0 0 0 rgba(22,139,255,0))", transform: "scale(1)" },
    "50%": { filter: "drop-shadow(0 0 7px rgba(22,139,255,.5))", transform: "scale(1.06)" },
  },
  "@keyframes routeReveal": {
    from: { opacity: 0, transform: "translate3d(0,10px,0)", filter: "blur(3px)" },
    to: { opacity: 1, transform: "translate3d(0,0,0)", filter: "blur(0)" },
  },
  root: {
    width: "100%",
    minHeight: "100vh",
    background: "var(--app-bg)",
    direction: theme.direction,
    transition: "background-color .25s ease",
  },

  desktopSidebar: {
    position: "fixed",
    zIndex: 1200,
    isolation: "isolate",
    overflow: "hidden",
    top: 0,
    bottom: 0,
    width: drawerWidth,
    left: theme.direction === "rtl" ? "auto" : 0,
    right: theme.direction === "rtl" ? 0 : "auto",
    borderRight: theme.direction === "rtl" ? "none" : "1px solid var(--border)",
    borderLeft: theme.direction === "rtl" ? "1px solid var(--border)" : "none",
    background:
      "linear-gradient(180deg,var(--sidebar-start) 0%,var(--page-bg-soft) 55%,var(--sidebar-end) 100%)",
    boxShadow:
      theme.direction === "rtl"
        ? "-8px 0 30px rgba(0,0,0,.035)"
        : "8px 0 30px rgba(0,0,0,.035)",
    "&::before": {
      content: '""',
      position: "absolute",
      zIndex: 0,
      width: 270,
      height: 270,
      borderRadius: "50%",
      left: theme.direction === "rtl" ? "auto" : -155,
      right: theme.direction === "rtl" ? -155 : "auto",
      top: "34%",
      pointerEvents: "none",
      background: "radial-gradient(circle,var(--accent-soft-2),rgba(22,139,255,.025) 42%,transparent 70%)",
      filter: "blur(2px)",
      animation: "$sidebarAura 8.5s ease-in-out infinite",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      zIndex: 0,
      width: 1,
      height: 86,
      top: 0,
      left: theme.direction === "rtl" ? 0 : "auto",
      right: theme.direction === "rtl" ? "auto" : 0,
      pointerEvents: "none",
      background: "linear-gradient(180deg,transparent,var(--accent),transparent)",
      boxShadow: "0 0 14px rgba(22,139,255,.5)",
      animation: "$sidebarScan 8s ease-in-out infinite 1.2s",
    },
  },

  sidebarInner: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },

  drawerPaper: {
    width: "min(88vw, 310px)",
    maxWidth: 310,
    height: "100dvh",
    color: "var(--text-primary)",
    direction: theme.direction,
    overflow: "hidden",
    background:
      "linear-gradient(180deg,var(--sidebar-start) 0%,var(--page-bg-soft) 55%,var(--sidebar-end) 100%)",
    borderRight: theme.direction === "rtl" ? "none" : "1px solid var(--border)",
    borderLeft: theme.direction === "rtl" ? "1px solid var(--border)" : "none",
    boxShadow:
      theme.direction === "rtl"
        ? "-18px 0 48px rgba(0,0,0,.22)"
        : "18px 0 48px rgba(0,0,0,.22)",
    isolation: "isolate",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      background: "radial-gradient(circle at 50% 44%,var(--accent-soft),transparent 32%)",
      animation: "$sidebarAura 7.5s ease-in-out infinite",
    },
  },

  profileArea: {
    position: "relative",
    zIndex: 2,
    flexShrink: 0,
    padding: "15px 13px 12px",
    borderBottom: "1px solid var(--border-soft)",
    "@media (max-width: 959px)": {
      padding: "10px 11px 9px",
    },
    "@media (max-height: 660px)": {
      padding: "7px 9px 6px",
    },
    "@media (max-height: 560px)": {
      padding: "4px 8px 3px",
    },
  },

  profileLink: {
    display: "grid",
    gridTemplateColumns: "66px minmax(0,1fr)",
    alignItems: "center",
    gap: 12,
    direction: theme.direction,
    minWidth: 0,
    padding: 8,
    borderRadius: 16,
    color: "inherit",
    border: "1px solid transparent",
    transition: "background-color .2s ease, border-color .2s ease, transform .2s ease",
    "&:hover": {
      background: "var(--surface-soft)",
      borderColor: "var(--border)",
      transform: "translateY(-1px)",
    },
    "@media (max-width: 959px)": {
      gridTemplateColumns: "58px minmax(0,1fr)",
      gap: 10,
      padding: 5,
    },
    "@media (max-height: 660px)": {
      gridTemplateColumns: "50px minmax(0,1fr)",
      gap: 8,
      padding: 3,
    },
    "@media (max-height: 560px)": {
      gridTemplateColumns: "44px minmax(0,1fr)",
      gap: 7,
      padding: 2,
    },
  },

  profileFrame: {
    position: "relative",
    width: 66,
    height: 66,
    padding: 3,
    borderRadius: "50%",
    background: "transparent",
    boxShadow: "0 12px 30px rgba(22,139,255,.18)",
    transition: "transform .28s cubic-bezier(.2,.8,.2,1), box-shadow .28s ease",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 0,
      borderRadius: "inherit",
      background: "conic-gradient(from 0deg,var(--accent),#66d7ff,var(--accent),#675cff,var(--accent))",
      animation: "$profileRingSpin 9s linear infinite",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: -5,
      zIndex: -1,
      borderRadius: "50%",
      background: "radial-gradient(circle,rgba(22,139,255,.28),transparent 66%)",
      filter: "blur(6px)",
      animation: "$profileGlow 4.2s ease-in-out infinite",
    },
    "$profileLink:hover &": {
      transform: "translateY(-2px) scale(1.025)",
      boxShadow: "0 16px 34px rgba(22,139,255,.24)",
    },
    "@media (max-width: 959px)": {
      width: 58,
      height: 58,
    },
    "@media (max-height: 660px)": {
      width: 50,
      height: 50,
    },
    "@media (max-height: 560px)": {
      width: 44,
      height: 44,
    },
  },

  profileImage: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "50% 35%",
    borderRadius: "50%",
    border: "3px solid var(--sidebar-start)",
  },

  profileCopy: {
    minWidth: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },

  profileName: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: 16,
    lineHeight: 1.45,
    marginBottom: 3,
    overflowWrap: "break-word",
    "@media (max-height: 700px)": { fontSize: 14 },
  },

  profileRole: {
    color: "var(--text-muted)",
    fontSize: 9,
    fontWeight: 800,
    lineHeight: 1.55,
    letterSpacing: theme.direction === "rtl" ? 0 : ".08em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
  },

  navigationArea: {
    position: "relative",
    zIndex: 2,
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    alignItems: "stretch",
    overflow: "hidden",
    padding: "10px 11px 6px",
    "@media (max-width: 959px)": {
      flex: "1 1 auto",
      padding: "6px 10px 5px",
    },
    "@media (max-height: 660px)": {
      padding: "3px 9px 2px",
    },
  },

  navList: {
    width: "100%",
    minHeight: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  navItem: {
    width: "100%",
    minHeight: 44,
    margin: "2px 0",
    padding: "0 14px",
    borderRadius: 13,
    color: "var(--text-muted)",
    position: "relative",
    direction: theme.direction,
    justifyContent: "flex-start",
    border: "1px solid transparent",
    overflow: "hidden",
    opacity: 0,
    animation: "$navEnter .48s cubic-bezier(.2,.75,.2,1) forwards",
    animationDelay: "var(--nav-delay, 0ms)",
    transition:
      "background-color .22s ease, color .22s ease, border-color .22s ease, transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease",
    "&:hover": {
      color: "var(--text-primary)",
      backgroundColor: "var(--surface-hover)",
      transform: theme.direction === "rtl" ? "translateX(-2px)" : "translateX(2px)",
    },
    "&:hover $navIcon": {
      color: "var(--accent)",
      transform: "scale(1.12) rotate(-4deg)",
      filter: "drop-shadow(0 0 7px rgba(22,139,255,.35))",
    },
    "@media (max-width: 959px)": {
      minHeight: 42,
      margin: "1px 0",
      padding: "0 12px",
    },
    "@media (max-height: 660px)": {
      minHeight: 36,
      margin: 0,
      padding: "0 10px",
    },
    "@media (max-height: 560px)": {
      minHeight: 32,
      padding: "0 9px",
    },
  },

  navItemActive: {
    color: "var(--text-primary)",
    borderColor: "var(--border-accent)",
    background:
      theme.direction === "rtl"
        ? "linear-gradient(270deg,var(--accent-soft-2),rgba(22,139,255,.025))"
        : "linear-gradient(90deg,var(--accent-soft-2),rgba(22,139,255,.025))",
    boxShadow: "var(--shadow-sm)",
    "&:before": {
      content: '\"\"',
      position: "absolute",
      top: 9,
      bottom: 9,
      width: 3,
      borderRadius: 10,
      background: "var(--accent)",
      left: theme.direction === "rtl" ? "auto" : 0,
      right: theme.direction === "rtl" ? 0 : "auto",
      boxShadow: "0 0 18px rgba(22,139,255,.45)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      zIndex: 0,
      top: 0,
      bottom: 0,
      width: "38%",
      left: 0,
      pointerEvents: "none",
      background: "linear-gradient(100deg,transparent,rgba(96,190,255,.20),transparent)",
      animation: "$activeSweep 4.8s ease-in-out infinite 1.1s",
    },
    "& $navIcon": {
      color: "var(--accent)",
      animation: "$activeIconPulse 3.2s ease-in-out infinite",
    },
  },

  navIcon: {
    width: 20,
    height: 20,
    flexShrink: 0,
    marginRight: theme.direction === "rtl" ? 0 : 14,
    marginLeft: theme.direction === "rtl" ? 14 : 0,
    position: "relative",
    zIndex: 1,
    transition: "color .2s ease, transform .22s cubic-bezier(.2,.8,.2,1), filter .22s ease",
    "@media (max-width: 959px)": {
      width: 18,
      height: 18,
    },
    "@media (max-height: 660px)": {
      width: 16,
      height: 16,
    },
  },

  navText: {
    position: "relative",
    zIndex: 1,
    minWidth: 0,
    color: "inherit",
    fontSize: 11.5,
    fontWeight: 800,
    lineHeight: 1.55,
    letterSpacing: theme.direction === "rtl" ? 0 : ".03em",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    "@media (max-width: 959px)": { fontSize: 11.5 },
    "@media (max-height: 660px)": { fontSize: 10.5 },
  },

  bottomArea: {
    position: "relative",
    zIndex: 2,
    flexShrink: 0,
    display: "grid",
    gridTemplateColumns: "64px minmax(0,1fr)",
    alignItems: "center",
    gap: 8,
    marginTop: 0,
    padding: "9px 11px 10px",
    borderTop: "1px solid var(--border-soft)",
    direction: theme.direction,
    "@media (max-width: 959px)": {
      gridTemplateColumns: "60px minmax(0,1fr)",
      marginTop: 2,
      padding: "8px 10px 9px",
    },
    "@media (max-height: 660px)": {
      padding: "6px 9px 7px",
    },
    "@media (max-height: 560px)": {
      padding: "4px 8px 5px",
    },
  },

  themeWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  languageSwitcher: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    direction: "ltr",
    gap: 3,
    padding: 3,
    borderRadius: 11,
    border: "1px solid var(--border)",
    backgroundColor: "var(--surface-soft)",
    minWidth: 0,
  },

  languageButton: {
    minWidth: 0,
    height: 31,
    borderRadius: 8,
    padding: "3px 7px",
    color: "var(--text-muted)",
    fontSize: 9,
    fontWeight: 850,
    letterSpacing: ".02em",
    "&:hover": {
      color: "var(--text-primary)",
      backgroundColor: "var(--accent-soft)",
    },
  },

  languageActive: {
    color: "#fff",
    background: "linear-gradient(135deg,#087ff5,#279bff)",
    boxShadow: "0 5px 16px rgba(22,139,255,.15)",
    "&:hover": { color: "#fff" },
  },

  mobileHeader: {
    position: "fixed",
    zIndex: 1250,
    top: 0,
    left: 0,
    right: 0,
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    direction: theme.direction,
    gap: 8,
    padding: "7px 10px",
    background: "color-mix(in srgb, var(--sidebar-start) 92%, transparent)",
    borderBottom: "1px solid var(--border)",
    backdropFilter: "blur(18px)",
  },

  mobileMenuButton: {
    width: 40,
    height: 40,
    flexShrink: 0,
    color: "var(--text-primary)",
    borderRadius: 11,
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
  },

  mobileBrand: {
    minWidth: 0,
    flex: 1,
    color: "var(--text-primary)",
    fontSize: 11.5,
    fontWeight: 900,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.down(420)]: { display: "none" },
  },

  mobileControls: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    direction: "ltr",
  },

  mobileLanguage: {
    flexShrink: 0,
    display: "flex",
    direction: "ltr",
    gap: 2,
    padding: 2,
    borderRadius: 9,
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
  },

  mobileLanguageButton: {
    minWidth: 34,
    height: 28,
    padding: "2px 4px",
    fontSize: 8,
    fontWeight: 850,
    color: "var(--text-muted)",
    borderRadius: 7,
  },

  mobileLanguageActive: {
    color: "#fff",
    background: "linear-gradient(135deg,#087ff5,#279bff)",
  },

  routeStage: {
    width: "100%",
    minHeight: "100vh",
    animation: "$routeReveal .48s cubic-bezier(.2,.75,.2,1) both",
    willChange: "transform, opacity, filter",
  },

  content: {
    width: "100%",
    minWidth: 0,
    minHeight: "100vh",
    position: "relative",
    overflowX: "hidden",
    background: "var(--page-bg)",
    transition: "background-color .25s ease",
    [theme.breakpoints.up("md")]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: theme.direction === "rtl" ? 0 : drawerWidth,
      marginRight: theme.direction === "rtl" ? drawerWidth : 0,
    },
    [theme.breakpoints.down("sm")]: { paddingTop: 60 },
  },
}));

const navItems = [
  { path: "/", exact: true, key: "home", icon: HomeIcon },
  { path: "/about", key: "about", icon: PersonOutlineIcon },
  { path: "/resume", key: "resume", icon: AssignmentOutlinedIcon },
  { path: "/portfolio", key: "portfolio", icon: WorkOutlineIcon },
  { path: "/blog", key: "blog", icon: ChromeReaderModeOutlinedIcon },
  { path: "/contact", key: "contact", icon: MailOutlineIcon },
];

function SiteShell({ window, themeMode, onThemeChange }) {
  const classes = useStyles();
  const translate = getTranslate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen((value) => !value);

  const activePage = (item) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const themeLabels = {
    light: translate.dayMode || "Day mode",
    dark: translate.darkMode || "Dark mode",
  };

  const LanguageSwitcher = ({ mobile = false }) => (
    <div className={mobile ? classes.mobileLanguage : classes.languageSwitcher}>
      <Button
        className={`${mobile ? classes.mobileLanguageButton : classes.languageButton} ${
          lang === "en"
            ? mobile
              ? classes.mobileLanguageActive
              : classes.languageActive
            : ""
        }`}
        onClick={() => changeLanguage("en")}
      >
        EN
      </Button>

      <Button
        className={`${mobile ? classes.mobileLanguageButton : classes.languageButton} ${
          lang === "fa"
            ? mobile
              ? classes.mobileLanguageActive
              : classes.languageActive
            : ""
        }`}
        onClick={() => changeLanguage("fa")}
        style={{ fontFamily: "IRANSans", letterSpacing: 0 }}
      >
        فارسی
      </Button>
    </div>
  );

  const drawerContent = (
    <div className={classes.sidebarInner}>
      <div className={classes.profileArea}>
        <Link to="/" className={classes.profileLink} onClick={() => setMobileOpen(false)}>
          <div className={classes.profileFrame}>
            <img src={ProfileImage} alt={translate.name} className={classes.profileImage} />
          </div>

          <div className={classes.profileCopy}>
            <Typography className={classes.profileName}>{translate.name}</Typography>
            <Typography className={classes.profileRole}>{translate.navRole}</Typography>
          </div>
        </Link>
      </div>

      <div className={classes.navigationArea}>
        <List className={classes.navList}>
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const active = activePage(item);

            return (
              <ListItem
                button
                key={item.path}
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`${classes.navItem} ${active ? classes.navItemActive : ""}`}
                style={{ "--nav-delay": `${110 + index * 58}ms` }}
                aria-current={active ? "page" : undefined}
              >
                <Icon className={classes.navIcon} />
                <Typography className={classes.navText}>{translate[item.key]}</Typography>
              </ListItem>
            );
          })}
        </List>
      </div>

      <div className={classes.bottomArea}>
        <div className={classes.themeWrap}>
          <ThemeToggle mode={themeMode} onChange={onThemeChange} labels={themeLabels} />
        </div>
        <LanguageSwitcher />
      </div>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <div className={classes.root}>
      <Hidden smDown implementation="css">
        <aside className={classes.desktopSidebar}>{drawerContent}</aside>
      </Hidden>

      <Hidden mdUp implementation="css">
        <div className={classes.mobileHeader}>
          <IconButton
            className={classes.mobileMenuButton}
            onClick={handleDrawerToggle}
            aria-label={translate.openNavigation}
          >
            <MenuIcon />
          </IconButton>

          <Typography className={classes.mobileBrand}>{translate.name}</Typography>

        </div>

        <Drawer
          container={container}
          variant="temporary"
          anchor={lang === "fa" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          PaperProps={{
            style:
              lang === "fa"
                ? { right: 0, left: "auto", direction: "rtl" }
                : { left: 0, right: "auto", direction: "ltr" },
          }}
          ModalProps={{ keepMounted: false }}
        >
          {drawerContent}
        </Drawer>
      </Hidden>

      <main className={classes.content}>
        <ScrollToTop />
        <div key={location.pathname} className={classes.routeStage}>
          <Switch>
            <Route exact path="/" component={HomeScreen} />
            <Route exact path="/about" component={AboutScreen} />
            <Route exact path="/resume" component={ResumeScreen} />
            <Route exact path="/portfolio" component={PortfolioScreen} />
            <Route exact path="/blog" component={BlogScreen} />
            <Route exact path="/contact" component={ContactScreen} />
            <Redirect to="/" />
          </Switch>
        </div>
      </main>
    </div>
  );
}

function ResponsiveDrawer(props) {
  return (
    <Router>
      <SiteShell {...props} />
    </Router>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
  themeMode: PropTypes.oneOf(["dark", "light"]),
  onThemeChange: PropTypes.func,
};

ResponsiveDrawer.defaultProps = {
  themeMode: "dark",
  onThemeChange: () => {},
};

export default ResponsiveDrawer;
